import { useMemo } from 'react';
import useAuth from '../../../../hooks/Auth/useAuth';
import { ROLES } from '../../../../redux/context/allowedTypes';

/**
 * Custom hook for managing statistics configuration and section visibility
 */
const useStatisticsConfig = () => {
  const { getConfigValue, getConfigOptions, role } = useAuth();

  // Helper function to get configuration options with fallback
  const getConfigOptionsWithFallback = (key) => {
    const options = getConfigOptions(key);
    if (options && options.length > 0) {
      return options;
    }
    
    // Fallback to hardcoded defaults if config not available
    switch (key) {
      case 'settings_default_date_range_days':
        return ['day', 'week', 'month'];
      case 'settings_default_time_periods':
        return [4, 6, 8, 12];
      default:
        return [];
    }
  };

  // Helper function to convert string values to appropriate types
  const getConfigValueWithDefault = (key, defaultValue) => {
    const configValue = getConfigValue(key);
    return configValue !== null ? configValue : defaultValue;
  };
  
  // Helper function to determine section visibility based on role and config
  const getSectionVisibility = (configKey, defaultValue = true) => {
    // CARTEAV_ADMINISTRATOR: All sections visible regardless of config
    if (role === ROLES.CARTEAV_ADMINISTRATOR) {
      return true;
    }
    
    // ADMIN or OPERATOR: Use config value or default to true
    if (role === ROLES.ADMIN || role === ROLES.OPERATOR) {
      return getConfigValueWithDefault(configKey, true);
    }
    
    // Other roles: Use regular config logic with provided default
    return getConfigValueWithDefault(configKey, defaultValue);
  };

  // Convert date range string to days
  const getDateRangeDays = () => {
    const rangeValue = getConfigValueWithDefault('settings_default_date_range_days', 'month');
    switch (rangeValue) {
      case 'day': return 1;
      case 'week': return 7;
      case 'month': return 30;
      default: return 30;
    }
  };

  // Helper function to determine if a quick select option is currently active
  const isQuickSelectActive = (option, dateRange) => {
    const getDaysFromOption = (option) => {
      switch (option) {
        case 'day': return 1;
        case 'week': return 7;
        case 'month': return 30;
        default: return 30;
      }
    };
    
    const optionDays = getDaysFromOption(option);
    const currentRangeDays = Math.round((dateRange[1] - dateRange[0]) / (1000 * 60 * 60 * 24));
    
    // Allow some tolerance for the comparison (±1 day)
    return Math.abs(currentRangeDays - optionDays) <= 1;
  };

  // Configuration-driven section visibility with role-based overrides
  const sectionVisibility = useMemo(() => ({
    cartSummary: getSectionVisibility('cart_summary_section_visible', true),
    demand: getSectionVisibility('demand_section_visible', true),
    availability: getSectionVisibility('availability_section_visible', true),
    onService: getSectionVisibility('onservice_section_visible', true),
    waitingTime: getSectionVisibility('waitingtime_section_visible', true),
    trips: getSectionVisibility('trips_section_visible', true)
  }), [role]);

  // Configuration-driven cart summary field visibility with role-based overrides
  const cartSummaryVisibility = useMemo(() => ({
    dailyTrips: getSectionVisibility('cart_summary_show_daily_trips', true),
    estopBrake: getSectionVisibility('cart_summary_show_estop_brake', true),
    pedalBrake: getSectionVisibility('cart_summary_show_pedal_brake', true),
    tripDuration: getSectionVisibility('cart_summary_show_trip_duration', true),
    dailyActiveTime: getSectionVisibility('cart_summary_show_daily_active_time', true),
    avgSpeed: getSectionVisibility('cart_summary_show_avg_speed', true),
    avgPassengersCount: getSectionVisibility('cart_summary_show_avg_passengers_count', true)
  }), [role]);

  // Default configuration values
  const defaultValues = useMemo(() => ({
    splitCount: getConfigValueWithDefault('settings_default_time_periods', 6),
    viewMode: getConfigValueWithDefault('default_view_mode', 'multi'),
    dateRangeDays: getDateRangeDays(),
    
    // Demand defaults
    demandFilterType: getConfigValueWithDefault('demand_default_filter_type', 'all'),
    selectedUserTypes: getConfigValueWithDefault('demand_default_user_types', ['all', 'regular']),
    userCreationPeriod: getConfigValueWithDefault('demand_default_creation_period', 'all'),
    
    // Availability defaults
    availabilityFilterType: getConfigValueWithDefault('availability_default_filter_type', 'all'),
    availabilityCartTypes: getConfigValueWithDefault('availability_default_cart_types', ['all']),
    
    // OnService defaults
    onServiceFilterType: getConfigValueWithDefault('onservice_default_filter_type', 'all'),
    onServiceCartTypes: getConfigValueWithDefault('onservice_default_cart_types', ['all', 'autonomous']),
    
    // WaitingTime defaults
    waitingTimeFilterType: getConfigValueWithDefault('waitingtime_default_filter_type', 'all'),
    waitingTimeCartTypes: getConfigValueWithDefault('waitingtime_default_cart_types', ['all']),
    
    // Trip defaults
    tripFilterType: getConfigValueWithDefault('trips_default_filter_type', 'all'),
    tripCartTypes: getConfigValueWithDefault('trips_default_cart_types', ['all', 'autonomous']),
    tripStatuses: getConfigValueWithDefault('trips_default_trip_statuses', ['success']),
    tripType: getConfigValueWithDefault('trips_default_trip_type', 'PATH_TYPE_TRANSPORTATION_GOTOPICKUP')
  }), []);

  return {
    getConfigOptionsWithFallback,
    getConfigValueWithDefault,
    getSectionVisibility,
    getDateRangeDays,
    isQuickSelectActive,
    sectionVisibility,
    cartSummaryVisibility,
    defaultValues
  };
};

export default useStatisticsConfig;






